<template>
  <Dialog
    :visible="visible"
    @update:visible="emit('update:visible', $event)"
    :closable="false"
    :modal="true"
    header="Apply Mid-roll Configuration"
    style="width: 600px"
  >
    <!-- Config Summary -->
    <div class="flex flex-column gap-3 mb-4">
      <div v-if="showTitle" class="text-sm text-500">
        Applying to: <span class="font-semibold text-500">{{ showTitle }}</span>
      </div>

      <div class="border-round surface-50 p-3">
        <div class="text-xs text-400 uppercase font-semibold mb-2">General</div>
        <div class="flex justify-content-between text-sm">
          <span class="text-600">Mid-rolls Limit</span>
          <span :class="midrollsLimit ? 'font-semibold' : 'text-400 italic'">
            {{ midrollsLimit ? midrollsLimit : "Not set" }}
          </span>
        </div>
      </div>

      <div class="border-round surface-50 p-3">
        <div class="text-xs text-400 uppercase font-semibold mb-2">
          First Mid-roll
        </div>
        <div class="flex flex-column gap-2">
          <div class="flex justify-content-between text-sm">
            <span class="text-600">Position (seconds)</span>
            <span
              :class="
                firstMidrollPosition ? 'font-semibold' : 'text-400 italic'
              "
            >
              {{ firstMidrollPosition ? firstMidrollPosition : "Not set" }}
            </span>
          </div>
          <div class="flex justify-content-between text-sm">
            <span class="text-600">Marker Limit</span>
            <span
              :class="
                firstMidrollMarkersLimit != null
                  ? 'font-semibold'
                  : 'text-400 italic'
              "
            >
              {{
                firstMidrollMarkersLimit != null
                  ? firstMidrollMarkersLimit
                  : "Not set"
              }}
            </span>
          </div>
          <div class="flex justify-content-between text-sm">
            <span class="text-600">Override existing mid-rolls</span>
            <span
              :class="
                overrideExistingMidrolls
                  ? 'font-semibold text-orange-500'
                  : 'text-400 italic'
              "
            >
              {{ overrideExistingMidrolls ? "Yes" : "No" }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <Divider class="my-3" />

    <!-- Confirm prompt -->
    <div
      v-if="phase === 'confirm'"
      class="text-center text-md tw-font-semibold"
    >
      Do you want to process midroll configurations for this show right now?
    </div>

    <!-- Progress / Result -->
    <div v-else class="flex flex-column gap-3 align-items-center">
      <ProgressSpinner
        v-if="phase === 'processing'"
        style="width: 36px; height: 36px"
        strokeWidth="4"
      />
      <i
        v-else-if="phase === 'completed'"
        class="pi pi-check-circle text-green-500"
        style="font-size: 2rem"
      ></i>
      <i
        v-else-if="phase === 'failed'"
        class="pi pi-times-circle text-red-500"
        style="font-size: 2rem"
      ></i>
      <span class="text-center text-sm text-600">{{ message }}</span>
    </div>

    <template #footer>
      <!-- Confirm phase: user decides yes or no -->
      <template v-if="phase === 'confirm'">
        <Button label="No, skip" text @click="emit('cancel')" />
        <Button label="Yes, apply now" @click="run" />
      </template>
      <!-- Processing: allow dismissing while job continues in background -->
      <template v-else-if="phase === 'processing'">
        <Button label="Run in Background" text @click="cancelAndDismiss" />
      </template>
      <!-- Done (completed or failed): close button -->
      <template v-else>
        <Button label="Close" @click="emit('close')" />
      </template>
    </template>
  </Dialog>
</template>

<script setup>
import Dialog from "primevue/dialog";
import Button from "primevue/button";
import Divider from "primevue/divider";
import ProgressSpinner from "primevue/progressspinner";
import axios from "axios";
import { ref } from "vue";
import { API_BASE_URL } from "@/consts";

// eslint-disable-next-line no-undef
const props = defineProps({
  visible: { type: Boolean, required: true },
  showTitle: { type: String, default: "" },
  showId: { type: [String, Number], required: true },
  token: { type: String, required: true },
  midrollsLimit: { type: Number, default: null },
  firstMidrollPosition: { type: Number, default: null },
  firstMidrollMarkersLimit: { type: Number, default: null },
  overrideExistingMidrolls: { type: Boolean, default: false },
});

// eslint-disable-next-line no-undef
const emit = defineEmits(["update:visible", "close", "cancel"]);

const phase = ref("confirm");
const message = ref("");
const pollInterval = ref(null);

const cancelAndDismiss = () => {
  if (pollInterval.value) {
    clearInterval(pollInterval.value);
    pollInterval.value = null;
  }
  emit("cancel");
};

const run = async () => {
  phase.value = "processing";
  message.value = "Queuing midroll application job...";

  try {
    const response = await axios.post(
      `${API_BASE_URL}/midrolls/apply-for-show/${props.showId}`,
      {},
      { headers: { Authorization: `Bearer ${props.token}` } }
    );

    if (!response.data?.success)
      throw new Error(response.data?.message || "Failed to create job");

    const jobId = response.data.data.jobId;
    message.value = "Processing...";

    pollInterval.value = setInterval(async () => {
      try {
        const statusResponse = await axios.get(
          `${API_BASE_URL}/midrolls/midroll-jobs/${jobId}`,
          { headers: { Authorization: `Bearer ${props.token}` } }
        );

        if (!statusResponse.data?.success) {
          clearInterval(pollInterval.value);
          throw new Error("Failed to fetch job status");
        }

        const status = statusResponse.data.data;

        if (status.state === "completed") {
          clearInterval(pollInterval.value);
          phase.value = "completed";
          message.value = "Configs applied successfully!";
        } else if (status.state === "failed") {
          clearInterval(pollInterval.value);
          phase.value = "failed";
          message.value = status.failedReason || "Failed to apply configs";
        }
      } catch (error) {
        clearInterval(pollInterval.value);
        phase.value = "failed";
        message.value = error.message || "Error checking job status";
      }
    }, 2000);
  } catch (error) {
    console.error("Error applying midrolls:", error);
    phase.value = "failed";
    message.value =
      error.response?.data?.message ||
      error.message ||
      "Failed to apply mid-rolls";
  }
};
</script>
